/* eslint-disable no-underscore-dangle */
// Service layer for Billingo invoicing.
//
// Talks to the real `/billingo` Lambda by default. Sub-actions route via an
// `action` query param (single API Gateway resource, mirroring the payments
// endpoint). Set REACT_APP_BILLINGO_MOCK=true to use the in-memory fixtures
// instead (handy for local UI work without a backend) — the components never change.
//
// Accounts are managed via Billingo's own search/create endpoints; we never
// build a local representation of them.

import { get, post } from './API';
import {
  documentTotals, bookingBreakdown, lineBreakdown,
} from '../Utils/billingoDocuments';
import {
  mockAccounts,
  mockDocumentsByBooking,
  nextDocumentNumber,
  nextAccountId,
} from './__fixtures__/billingo';

// Mock is OPT-IN: set REACT_APP_BILLINGO_MOCK=true to run the flow against the
// in-memory fixtures with no backend. Otherwise every environment — `yarn
// start` included — talks to the real `/billingo` Lambda, like the rest of the
// dashboard's services do. It defaulted to on in development, which silently
// showed an empty Invoicing page against real credentials.
//
// NOTE: with the mock off, creating a document in local dev issues a REAL
// Billingo document against whatever API REACT_APP_API_URL points at.
const USE_MOCK = process.env.REACT_APP_BILLINGO_MOCK === 'true';
const MOCK_LATENCY_MS = 300;

// Drop empty filters so they never reach the API as `?status=`.
const compactQuery = (params = {}) => Object.fromEntries(
  Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
);

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Per-attempt dedupe key so a retried request can't issue a duplicate fiscal
// document (the backend reserves/dedupes on it).
export const makeIdempotencyKey = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `idk-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const matchesQuery = (account, q) => {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [account.name, account.tax_number, ...(account.emails || [])]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(needle));
};

// Pick the document's single currency + total from its (possibly mixed) lines.
// A real Billingo document is single-currency; the UI warns on mixed selections,
// so here we settle on the first currency present.
const summarizeLines = (lineItems) => {
  const totals = documentTotals(lineItems);
  const currency = totals.currencies[0] || 'EUR';
  const gross = totals.byCurrency[currency]?.gross ?? 0;
  return { currency, total: Number(gross.toFixed(2)) };
};

// Minimal booking context for a mock document, synthesized from the line items
// (the fixtures have no Booking collection to join against).
const mockBookingContext = (lineItems = []) => {
  const seen = new Map();
  lineItems.filter((l) => l && l.booking_id && !l.deduction).forEach((l) => {
    if (!seen.has(l.booking_id)) {
      seen.set(l.booking_id, {
        booking_id: l.booking_id,
        booking_reference: l.order_reference,
      });
    }
  });
  return [...seen.values()];
};

const storeDocument = (bookingIds, doc) => {
  (bookingIds && bookingIds.length ? bookingIds : ['__unassigned__']).forEach((id) => {
    if (!mockDocumentsByBooking[id]) mockDocumentsByBooking[id] = [];
    mockDocumentsByBooking[id].push(doc);
  });
};

// Collect documents across many bookings, deduped by id. A multi-booking
// document is stored under each contributing booking, so it must be counted
// once for aggregation; its booking_breakdown handles per-booking attribution.
const collectMockDocuments = (bookings) => {
  const seen = new Set();
  const out = [];
  (bookings || []).forEach((booking) => {
    (mockDocumentsByBooking[booking._id] || []).forEach((doc) => {
      if (!seen.has(doc.id)) { seen.add(doc.id); out.push(doc); }
    });
  });
  return out;
};

const getDocumentsForBookingIds = async (ids) => {
  const response = await get('/billingo', { booking_ids: ids.join(',') });
  return response.data?.data || [];
};

export default {
  // --- Billingo accounts (partners) -------------------------------------
  searchAccounts: async (query = '') => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      return mockAccounts.filter((a) => matchesQuery(a, query));
    }
    const response = await get('/billingo', { action: 'search-accounts', query });
    return response.data?.data || [];
  },

  createAccount: async (payload) => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      const account = { id: nextAccountId(), ...payload };
      mockAccounts.push(account);
      return account;
    }
    const response = await post('/billingo', payload, {}, { action: 'create-account' });
    return response.data?.data;
  },

  // --- Documents --------------------------------------------------------
  // Accepts the order/booking object (needs _id for lookup). Returns every
  // document for the order: the Billingo ones and the uploaded PDF, if any
  // (a 'legacy' row, its `pdf_url` a short-lived signed link).
  listDocumentsForBooking: async (order) => {
    const bookingId = order?._id;
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      return (bookingId && mockDocumentsByBooking[bookingId]) || [];
    }
    const response = await get('/billingo', { booking_id: bookingId });
    return response.data?.data || [];
  },

  // Deduped documents across many bookings — powers the Invoicing module's
  // invoiced/remaining summary for a booking set (by-bookings, by-event,
  // by-partner).
  listDocumentsForBookings: async (bookings = []) => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      return collectMockDocuments(bookings);
    }
    return getDocumentsForBookingIds(bookings.map((b) => b._id).filter(Boolean));
  },

  // The same, for rows that carry booking ids rather than orders — one request
  // for a whole page of the Invoicing lists.
  listDocumentsForBookingIds: async (ids = []) => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      return collectMockDocuments(ids.map((_id) => ({ _id })));
    }
    return getDocumentsForBookingIds(ids);
  },

  // One page of the Invoicing module's "All invoices" list.
  //
  // `params` accepts page, limit, type, status, created_by, currency, from, to,
  // min_total, max_total, q and `uploads` — uploaded PDFs are left out (the
  // list AND its KPI summary) unless it is true. Returns the page plus the
  // pagination envelope, the KPI summary and the values available to filter by.
  listAllDocuments: async ({ uploads = false, ...params } = {}) => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      const seen = new Set();
      const out = [];
      Object.values(mockDocumentsByBooking).forEach((arr) => arr.forEach((doc) => {
        if (!seen.has(doc.id)) { seen.add(doc.id); out.push(doc); }
      }));
      return {
        documents: out,
        pagination: {
          page: 1, limit: out.length || 1, total: out.length, pages: 1,
        },
        summary: {
          total: out.length, awaiting: 0, paid: 0, this_month: 0,
        },
        facets: { created_by: [], currencies: [] },
        paginated: true,
      };
    }
    const response = await get('/billingo', compactQuery({
      ...params,
      // `source` absent means every source.
      source: uploads ? '' : 'billingo',
    }));
    // `response.data` IS the controller's return value — { data, pagination,
    // summary, facets } here, matching PaymentsService.list.
    const body = response.data || {};
    const documents = body.data || [];

    // An API that predates pagination answers with { data } only. Serve it as a
    // single page so the list still works, and flag it so the UI can say so
    // rather than showing a pager that cannot page.
    if (!body.pagination) {
      return {
        documents,
        pagination: {
          page: 1, limit: documents.length || 1, total: documents.length, pages: 1,
        },
        summary: null,
        facets: { created_by: [], currencies: [] },
        paginated: false,
      };
    }

    return {
      documents,
      pagination: body.pagination,
      summary: body.summary || null,
      facets: body.facets || { created_by: [], currencies: [] },
      paginated: true,
    };
  },

  // Past Confirmed/Completed bookings still owed a document (the Invoicing
  // "Missing documents" tab), filtered and paged by the API.
  listMissingDocuments: async (params = {}) => {
    const empty = {
      rows: [],
      pagination: {
        page: 1, limit: params.limit || 50, total: 0, pages: 1,
      },
      summary: {
        total: 0, no_documents: 0, no_szamla: 0, hidden: 0,
      },
      facets: { partners: [], events: [] },
      stats: null,
      stale: false,
    };
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      return empty;
    }
    const response = await get('/billingo', compactQuery({ ...params, action: 'missing-documents' }));
    const body = response.data || {};
    // An API without this action falls through to the invoice list, whose
    // summary has no per-kind counts. Say so instead of rendering invoices as
    // bookings.
    if (!body.summary || !('no_documents' in body.summary)) {
      return { ...empty, stale: true };
    }
    return {
      rows: body.data || [],
      pagination: body.pagination || empty.pagination,
      summary: body.summary,
      facets: body.facets || empty.facets,
      // Chart data; absent from an API that predates the visual backlog.
      stats: body.stats || null,
      stale: false,
    };
  },

  // Every Billingo document operation that did not complete — issue, send,
  // storno… — with Billingo's own reason (the Invoicing "Failed documents"
  // tab), paged by the API. `summary.groups_still_missing_document` counts the
  // order groups whose refused document still does not exist.
  listFailedDocuments: async (params = {}) => {
    const empty = {
      rows: [],
      pagination: {
        page: 1, limit: params.limit || 50, total: 0, pages: 1,
      },
      summary: {
        total: 0, groups_still_missing_document: 0, order_groups: 0, exposure: {}, unnotified: 0,
      },
      stale: false,
    };
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      return empty;
    }
    const response = await get('/billingo', compactQuery({ ...params, action: 'failed-documents' }));
    const body = response.data || {};
    // An API without this action falls through to the invoice list; say so
    // rather than render invoices as failures.
    if (!body.summary || !('groups_still_missing_document' in body.summary)) {
      return { ...empty, stale: true };
    }
    return {
      rows: body.data || [],
      pagination: body.pagination || empty.pagination,
      summary: body.summary,
      stale: false,
    };
  },

  // Hide (hidden=true) or unhide bookings from Missing documents, for everyone.
  // Hiding carries the `reason` the API requires; unhiding sends none.
  setMissingDocumentsHidden: async (bookingIds, hidden, reason = '') => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      return { booking_ids: bookingIds, hidden };
    }
    const response = await post(
      '/billingo',
      { booking_ids: bookingIds, hidden, ...(hidden ? { reason } : {}) },
      {},
      { action: 'hide-missing-documents' },
    );
    return response.data?.data;
  },

  // payload: { type, account, lineItems, booking_ids, order_references, payment_id? }
  createDocument: async (payload) => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      const { currency, total } = summarizeLines(payload.lineItems || []);
      const number = nextDocumentNumber(payload.type);
      const doc = {
        id: `doc_${number}`,
        type: payload.type,
        number,
        pdf_url: `https://mock.billingo.hu/documents/${number}.pdf`,
        total,
        currency,
        status: payload.type === 'dijbekero' ? 'awaiting_payment' : 'issued',
        created_at: new Date().toISOString(),
        billingo_account_id: payload.account?.id || null,
        billingo_document_id: Date.now(),
        order_references: payload.order_references || [],
        line_items: payload.lineItems || [],
        // Per-booking and per-line allocation of this document's net total, so
        // invoiced totals stay correct across multi-booking docs, advance
        // deductions, and the per-order-line reconciliation view.
        booking_breakdown: bookingBreakdown(payload.lineItems || [], total),
        line_breakdown: lineBreakdown(payload.lineItems || [], total),
        payment_id: payload.payment_id || null,
        booking_ids: payload.booking_ids || [],
        created_by: 'you@pickuphungary.com',
        fulfillment_date: payload.fulfillment_date,
        due_date: payload.due_date,
        payment_method: payload.payment_method,
        payment_date: payload.payment_date,
        advance_invoice_ids: [],
        last_sent_to: [],
        bookings: mockBookingContext(payload.lineItems || []),
      };
      storeDocument(payload.booking_ids, doc);
      return doc;
    }
    const response = await post('/billingo', { ...payload, idempotency_key: makeIdempotencyKey() });
    return response.data?.data;
  },

  // Fetch a Billingo document's PDF (base64) via the authenticated backend
  // proxy. Returns { content_base64, content_type }.
  getDocumentPdf: async (billingoDocumentId) => {
    const response = await get('/billingo', { action: 'download', id: billingoDocumentId });
    return response.data?.data;
  },

  // Email a document (PDF attached) to one or more recipients. One email per
  // document, even when it spans multiple bookings.
  sendDocument: async ({ billingo_document_id: billingoDocumentId, emails }) => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      return { sent_to: emails };
    }
    const response = await post(
      '/billingo',
      { billingo_document_id: billingoDocumentId, emails },
      {},
      { action: 'send' },
    );
    return response.data?.data;
  },

  // Issue a final Számla from an existing Díjbekérő (proforma) via Billingo's
  // native create-from-proforma endpoint. The caller owns `idempotency_key`
  // (makeIdempotencyKey): one per attempt, so a double-click issues once.
  createFromProforma: async ({
    billingo_document_id: billingoDocumentId, payment_id: paymentId,
    idempotency_key: idempotencyKey,
  }) => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      // Find the source Díjbekérő among the stored mock documents and synthesize
      // a Számla mirroring its bookings/lines/total.
      let source = null;
      Object.values(mockDocumentsByBooking).forEach((arr) => arr.forEach((d) => {
        if (d.billingo_document_id === billingoDocumentId) source = d;
      }));
      const number = nextDocumentNumber('szamla');
      const doc = {
        id: `doc_${number}`,
        type: 'szamla',
        number,
        pdf_url: `https://mock.billingo.hu/documents/${number}.pdf`,
        total: source?.total ?? 0,
        currency: source?.currency ?? 'EUR',
        status: 'issued',
        created_at: new Date().toISOString(),
        billingo_account_id: source?.billingo_account_id || null,
        billingo_document_id: Date.now(),
        order_references: source?.order_references || [],
        line_items: source?.line_items || [],
        booking_breakdown: source?.booking_breakdown || {},
        line_breakdown: source?.line_breakdown || {},
        payment_id: source?.payment_id || null,
        booking_ids: source?.booking_ids || [],
        created_by: 'you@pickuphungary.com',
        advance_invoice_ids: [],
        last_sent_to: [],
        bookings: source?.bookings || [],
      };
      const bookingIds = source
        ? Object.keys(source.booking_breakdown || {})
        : [];
      storeDocument(bookingIds, doc);
      return doc;
    }
    const response = await post(
      '/billingo',
      {
        billingo_document_id: billingoDocumentId,
        payment_id: paymentId || null,
        idempotency_key: idempotencyKey,
      },
      {},
      { action: 'create-from-proforma' },
    );
    return response.data?.data;
  },

  // --- Stripe-payment flow ---------------------------------------------
  assignPaymentToOrder: async (paymentId, orderId) => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      return { success: true, payment_id: paymentId, order_id: orderId };
    }
    const response = await post(
      '/billingo',
      { payment_id: paymentId, order_id: orderId },
      {},
      { action: 'assign-payment' },
    );
    return response.data?.data;
  },

  // Cancel (storno) an issued document. Typically after a refund.
  cancelDocument: async ({ billingo_document_id: billingoDocumentId, reason }) => {
    if (USE_MOCK) {
      await delay(MOCK_LATENCY_MS);
      Object.values(mockDocumentsByBooking).forEach((arr) => arr.forEach((d) => {
        if (d.billingo_document_id === billingoDocumentId) Object.assign(d, { status: 'cancelled' });
      }));
      return { cancelled: true };
    }
    const response = await post(
      '/billingo',
      { billingo_document_id: billingoDocumentId, reason },
      {},
      { action: 'cancel' },
    );
    return response.data?.data;
  },
};
